<template>
  <div
    v-if="(inline || $route.name !== 'selfie') && (support || tutorial)"
    class="assistance-actions"
    :class="{ 'assistance-actions--inline': inline }"
  >
    <button
      v-if="tutorial"
      class="assistance-action assistance-action--help"
      type="button"
      aria-label="Abrir vídeo de ajuda"
      @click="helpDialog = true"
    >
      <span class="assistance-action__label">Ajuda</span>
      <v-icon color="white" size="38">mdi-play-circle-outline</v-icon>
    </button>

    <button
      v-if="support"
      class="assistance-action assistance-action--whatsapp"
      type="button"
      aria-label="Abrir contato de suporte pelo WhatsApp"
      @click="openSupport"
    >
      <span class="assistance-action__label">Suporte</span>
      <v-icon color="white" size="38">mdi-whatsapp</v-icon>
    </button>

    <v-dialog v-if="support" v-model="supportDialog" max-width="620" persistent>
      <v-card class="assistance-dialog">
        <button
          class="assistance-close"
          type="button"
          aria-label="Fechar contato de suporte"
          @click="supportDialog = false"
        >
          <v-icon size="38">mdi-close</v-icon>
        </button>
        <div class="assistance-dialog__icon assistance-dialog__icon--whatsapp">
          <v-icon color="white" size="52">mdi-whatsapp</v-icon>
        </div>
        <h2>Precisa de ajuda?</h2>
        <p>
          Aponte a câmera do celular para falar com
          <strong>{{ support.name }}</strong
          >.
        </p>
        <div class="assistance-qr">
          <img
            v-if="supportQrCode"
            :src="supportQrCode"
            alt="QR Code para abrir o suporte no WhatsApp"
          />
          <p v-else-if="supportQrError" role="alert">
            Não foi possível gerar o QR. Use o número abaixo.
          </p>
          <v-progress-circular v-else indeterminate color="#ff006b" />
        </div>
        <div class="assistance-phone">{{ formattedSupportPhone }}</div>
      </v-card>
    </v-dialog>

    <TotemHelpVideoDialog
      v-if="tutorial"
      v-model="helpDialog"
      :tutorial="tutorial"
    />
  </div>
</template>

<script>
import QRCode from "qrcode";
import { fetchTotemDeviceConfig } from "@/service/totemDeviceConfig";
import TotemHelpVideoDialog from "@/components/assistance/TotemHelpVideoDialog.vue";
import qrDialogTimeout from "@/mixins/qrDialogTimeout";

export default {
  name: "TotemAssistanceActions",
  mixins: [qrDialogTimeout("supportDialog")],
  components: { TotemHelpVideoDialog },
  props: {
    inline: { type: Boolean, default: false },
  },
  data() {
    return {
      config: null,
      helpDialog: false,
      supportDialog: false,
      supportQrCode: "",
      supportQrError: false,
      refreshTimer: null,
      destroyed: false,
    };
  },
  computed: {
    totemId() {
      return (
        this.$store.getters["totem/getToken"] || localStorage.getItem("totemId")
      );
    },
    support() {
      return this.config && this.config.support;
    },
    tutorial() {
      const tutorial = this.config && this.config.tutorial;
      return tutorial && tutorial.videoUrl ? tutorial : null;
    },
    formattedSupportPhone() {
      const digits = String(this.support?.displayPhone || "").replace(
        /\D/g,
        ""
      );
      if (digits.length === 13 && digits.startsWith("55")) {
        return `+55 (${digits.slice(2, 4)}) ${digits.slice(
          4,
          9
        )}-${digits.slice(9)}`;
      }
      if (digits.length === 12 && digits.startsWith("55")) {
        return `+55 (${digits.slice(2, 4)}) ${digits.slice(
          4,
          8
        )}-${digits.slice(8)}`;
      }
      return digits ? `+${digits}` : "";
    },
  },
  watch: {
    totemId(value, previousValue) {
      if (value && value !== previousValue) this.loadConfiguration(true);
    },
    $route() {
      this.helpDialog = false;
      this.supportDialog = false;
      this.loadConfiguration();
    },
  },
  mounted() {
    this.loadConfiguration();
    this.scheduleRefresh();
  },
  beforeDestroy() {
    this.destroyed = true;
    clearTimeout(this.refreshTimer);
  },
  methods: {
    async loadConfiguration(force = false) {
      if (!this.totemId) return;
      try {
        const next = await fetchTotemDeviceConfig(this.totemId, { force });
        const previousSupport = this.support && this.support.whatsappUrl;
        this.config = next;
        if (previousSupport !== (this.support && this.support.whatsappUrl)) {
          this.closeQrDialog();
          this.supportQrCode = "";
          this.supportQrError = false;
        }
        if (!this.support) this.supportDialog = false;
        if (!this.tutorial) this.helpDialog = false;
      } catch (error) {
        // O suporte é complementar: indisponibilidade da API não pode parar a cabine.
        console.warn("Configuração de suporte do totem indisponível.", error);
      }
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(async () => {
        if (this.destroyed) return;
        await this.loadConfiguration(true);
        this.scheduleRefresh();
      }, 5 * 60 * 1000);
    },
    async openSupport() {
      if (!this.support || this.destroyed) return;
      const url = this.support.whatsappUrl;
      const generation = this.openQrDialog();
      if (this.supportQrCode) return;
      this.supportQrError = false;
      try {
        const qrCode = await QRCode.toDataURL(url, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 360,
        });
        if (
          this.isQrDialogRequestCurrent(generation) &&
          this.support?.whatsappUrl === url
        ) {
          this.supportQrCode = qrCode;
        }
      } catch (error) {
        if (!this.isQrDialogRequestCurrent(generation)) return;
        this.supportQrError = true;
        console.warn("Não foi possível gerar o QR de suporte.", error);
      }
    },
  },
};
</script>

<style scoped>
.assistance-actions {
  display: flex;
  gap: 14px;
  position: fixed;
  left: 28px;
  top: 32px;
  z-index: 120;
}

.assistance-actions--inline {
  flex-wrap: wrap;
  position: static;
}

.assistance-action--help {
  background: #ff006b;
}

.assistance-action {
  align-items: center;
  border: 4px solid rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  box-shadow: 0 10px 28px rgba(31, 38, 76, 0.24);
  color: #fff;
  display: flex;
  font-size: 24px;
  font-weight: 700;
  gap: 12px;
  min-height: 74px;
  justify-content: center;
  padding: 0 22px 0 26px;
}

.assistance-action--whatsapp {
  background: #25d366;
}

.assistance-action__label {
  line-height: 1;
}

.assistance-dialog {
  align-items: center;
  border-radius: 30px !important;
  color: #2c3763;
  display: flex;
  flex-direction: column;
  padding: 48px 46px 42px;
  position: relative;
  text-align: center;
}

.assistance-close {
  background: transparent;
  border: 0;
  position: absolute;
  right: 20px;
  top: 18px;
}

.assistance-dialog__icon {
  align-items: center;
  border-radius: 50%;
  display: flex;
  height: 84px;
  justify-content: center;
  width: 84px;
}

.assistance-dialog__icon--whatsapp {
  background: #25d366;
}

.assistance-dialog h2 {
  font-size: 36px;
  margin: 24px 0 10px;
}

.assistance-dialog p {
  font-size: 22px;
  line-height: 1.45;
  margin: 0 0 24px;
  max-width: 470px;
}

.assistance-qr {
  align-items: center;
  background: #fff;
  border: 2px solid #ebeaf1;
  border-radius: 22px;
  display: flex;
  height: 380px;
  justify-content: center;
  padding: 10px;
  width: 380px;
}

.assistance-qr img {
  display: block;
  height: 360px;
  width: 360px;
}

.assistance-qr p {
  color: #a12622;
  font-size: 19px;
  margin: 0;
  max-width: 300px;
}

.assistance-phone {
  background: #f6f7fb;
  border-radius: 16px;
  font-size: 24px;
  font-weight: 700;
  margin-top: 24px;
  padding: 16px 24px;
  width: 100%;
}
</style>
